import React, { useContext, useEffect, useMemo } from 'react';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import {
	Alert,
	Button,
	Flex,
	Group,
	TextInput as MantineTextInput,
	Stack,
	Text,
	Tooltip,
} from '@mantine/core';
import { FormProvider } from 'react-hook-form';
import { NumberInput, Select, TextInput } from 'react-hook-form-mantine';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns.tsx';
import {
	getSecuritiesOptionsForSelect,
	TRANSACTION_PARTICIPATION_THRESHOLD_EXPRESSED_AS_OPTIONS,
	TRANSACTION_TYPE_OPTIONS,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/common/utils.ts';
import {
	EDIT_TRANSACTION_FORM_FIELD,
	useEditTransactionDrawerController,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/EditTransactionDrawer/useEditTransactionDrawerController.ts';
import { MantineDateInput } from '@dealstack/frontend/shared/components/mantineForm/MantineDateInput';
import {
	DataSecurityTct,
	PermissionRole,
	TransactionType,
	TransactionWithSecurityTct,
} from '@dealstack/shared/lib/generated';
import {
	DATE_FORMATS,
	formatBEDateToUiWithFallback,
} from '@dealstack/shared/lib/format/dates/date.ts';
import { formatNumber } from '@dealstack/shared/lib/format/number';
import { usePermissionRequest } from '@dealstack/frontend/shared/components/PermissionWrapper/PermissionWrapper.tsx';
import { ResourceType } from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions.ts';
import {
	defaultToEmptyString,
	normalizeScreamingSnakeToUI,
} from '@dealstack/shared/lib/format/string.ts';
import { DealContext } from '@dealstack/frontend/app/pages/Project/DealDashboard/DealContext';
import { ScrollArea } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/TransactionDetails/styled';
import {
	getEntityNameById,
	getMipName,
	getSecurityNameById,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/TransactionDetails/utils.ts';
import { CopyableDetailValue } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/TransactionDetails/TransactionOverview/CopyableDetailValue.tsx';
import { getTransactionInterestText } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/common/getTransactionInterestText.ts';
import { getTransactionTypeText } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/common/getTransactionTypeText.ts';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';
import { EditableInfoField } from '@dealstack/frontend/shared/components/EditableInfoField/EditableInfoField.tsx';
import { EditableInfoContainer } from '@dealstack/frontend/shared/components/EditableInfoContainer/EditableInfoContainer.tsx';
import { InterestPairEditModal } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/InterestPairEditModal/InterestPairEditModal.tsx';
import { InseparableRedemptionModal } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/InseparableRedemptionModal/InseparableRedemptionModal.tsx';
import { RedemptionCounterpartPanel } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/RedemptionCounterpartPanel/RedemptionCounterpartPanel.tsx';
import { shouldShowHoldsFor } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/hooks/useTransactionFieldsVisibility';
import { isManagementLoanSecurity } from '@dealstack/frontend/shared/cap';

interface Props {
	transaction?: TransactionWithSecurityTct;
	transactionId?: string;
	onSplitClick: () => void;
	onPairClick: () => void;
	// CAP-2011: "Update interest transferred" — only rendered for a Both-titled
	// TRANSFER/REDEMPTION/CANCELLATION on a nominee-structure security.
	onSplitInterestClick?: () => void;
	showSplitInterest?: boolean;
	securities?: DataSecurityTct[];
	personSelectOptions: {
		label: string;
		value: string;
	}[];
	mipSelectOptions: {
		label: string;
		value: string;
	}[];
	refetchTransactionDetails?: (transactionId: string) => void;
	// CAP-1976: an interest edit Both -> single on a REDEMPTION/CANCELLATION replaces this row
	// with the chosen-title twin — the page navigates to the returned id.
	onTransactionReplaced?: (newTransactionId: string) => void;
	// CAP-1976: escape hatch out of a blocked interest-pair edit. Opens the page's own delete
	// confirmation, which already spells out that the linked leg goes with it.
	onDeletePair: () => void;
}

export const TransactionOverview = ({
	transaction,
	transactionId,
	onSplitClick,
	onPairClick,
	onSplitInterestClick,
	showSplitInterest,
	securities,
	personSelectOptions,
	mipSelectOptions,
	refetchTransactionDetails,
	onTransactionReplaced,
	onDeletePair,
}: Props) => {
	const tCapManage = useTranslationCapManage();
	const { dealId } = useContext(DealContext) || {};

	const {
		editTransactionForm,
		onSubmitDetails,
		sellerMipSelectOptions,
		editMode,
		setEditMode,
		showInterestSelect,
		operationInterestOptions,
		pairInterestOptions,
		operationRailNote,
		interestFixedByRedemptionGroup,
		interestFixedReason,
		nomineeStructuresEnabled,
		hideBoughtFor,
		hideSoldFor,
		boughtForOptions,
		soldForOptions,
		hideUnits,
		hideSeller,
		hideBuyer,
		hideBuyerMip,
		hideSellerMip,
		hideCost,
		costLabel,
		unitsDecimalPoints,
		interestPairModal,
		closeInterestPairModal,
		selectedSecurity,
		// CAP-1976: this page is the other edit surface for the same form, so a leg of a resolved
		// redemption has to open as the OPERATION here too — otherwise the submit resolves a preview
		// nothing renders and the save looks like it silently did nothing.
		isRedemptionOperation,
		redemptionCounterparts,
		redemptionCounterpartPreview,
		redemptionPreviewInterest,
		redemptionSegmentCount,
		pendingRedemption,
		confirmRedemptionOperation,
		cancelRedemptionOperation,
		isRedemptionOperationPending,
	} = useEditTransactionDrawerController({
		transaction,
		refetchTransactionDetails,
		securities,
		onTransactionReplaced,
	});

	const { hasPermission: hasEditorPermission } = usePermissionRequest({
		resourceType: ResourceType.DEAL,
		resourceId: defaultToEmptyString(dealId),
		permissionRole: PermissionRole.EDITOR,
	});

	// The transaction's own security is added when the entity-scoped list misses it (a holder's page),
	// so the select can render its current value instead of an empty box.
	const isManagementLoan = isManagementLoanSecurity(selectedSecurity);

	const securityNameSelectOptions = useMemo(() => {
		const options = getSecuritiesOptionsForSelect(securities);
		if (!selectedSecurity || options.some((option) => option.value === selectedSecurity.id)) {
			return options;
		}
		return [...options, { value: selectedSecurity.id, label: selectedSecurity.name }];
	}, [securities, selectedSecurity]);

	const transactionDateText = formatBEDateToUiWithFallback(
		transaction?.date,
		DATE_FORMATS.MONTH_DAY_YEAR,
		LONG_DASH
	);

	// CAP-1062: the feature flag keeps every nominee surface dark, this row included. With the flag
	// on, undefined only when the transaction carries no title at all — the one other case the
	// Interest row stays hidden, since there is nothing to show and nothing the dropdown could
	// preserve.
	const interestText = nomineeStructuresEnabled
		? getTransactionInterestText(transaction?.title, tCapManage)
		: undefined;

	const buyerMip = editTransactionForm.watch(EDIT_TRANSACTION_FORM_FIELD.BUYER_MIP);
	const sellerMip = editTransactionForm.watch(EDIT_TRANSACTION_FORM_FIELD.SELLER_MIP);
	const transactionType = editTransactionForm.watch(EDIT_TRANSACTION_FORM_FIELD.TYPE);

	const transactionUnits = transaction
		? formatNumber({
				value: transaction.units ?? 0,
				lockPrecision: true,
				minPrecision: unitsDecimalPoints,
				maxPrecision: unitsDecimalPoints,
			})
		: LONG_DASH;

	const formTransactionType = editTransactionForm.watch(EDIT_TRANSACTION_FORM_FIELD.TYPE);
	const securityParticipationThreshold = !!transaction?.security?.residueParticipationThreshold;

	const shouldDisablePairButton =
		transaction?.type !== TransactionType.TRANSFER ||
		(transaction?.sourceId === undefined && transaction?.destinationId === undefined);
	const disableParticipationThresholdFields =
		formTransactionType !== TransactionType.ISSUE || !securityParticipationThreshold;

	const participationThresholdExpressedAsLabelText = tCapManage(
		'transactions.drawer.transaction_participationThresholdExpressedAs.label'
	);
	const participationThresholdLabelText = tCapManage(
		'transactions.drawer.transaction_participationThreshold.label'
	);
	const participationThresholdExpressedAsLabel = disableParticipationThresholdFields ? (
		<LabelWithTooltip
			labelText={participationThresholdExpressedAsLabelText}
			tooltipLabel={tCapManage(
				'transactions.drawer.transaction_participationThresholdExpressedAs.tooltip'
			)}
		/>
	) : (
		participationThresholdExpressedAsLabelText
	);
	const participationThresholdLabel = disableParticipationThresholdFields ? (
		<LabelWithTooltip
			labelText={participationThresholdLabelText}
			tooltipLabel={tCapManage(
				'transactions.drawer.transaction_participationThreshold.tooltip'
			)}
		/>
	) : (
		participationThresholdLabelText
	);

	const handleCancel = () => {
		setEditMode(false);
	};

	useEffect(() => {
		const noMip = mipSelectOptions?.find((buyerMip) => !buyerMip.value);
		if (!buyerMip && noMip) {
			editTransactionForm.setValue(EDIT_TRANSACTION_FORM_FIELD.BUYER_MIP, noMip.value);
		}
	}, [buyerMip, mipSelectOptions, editTransactionForm]);

	useEffect(() => {
		const noMip = sellerMipSelectOptions.find((sellerMip) => !sellerMip.value);
		if (!sellerMip && noMip) {
			editTransactionForm.setValue(EDIT_TRANSACTION_FORM_FIELD.SELLER_MIP, noMip.value);
		}
	}, [sellerMip, sellerMipSelectOptions, editTransactionForm]);

	useEffect(() => {
		const currentValues = editTransactionForm.getValues();
		const formattedValue = Number(currentValues.units?.toFixed(unitsDecimalPoints));
		editTransactionForm.setValue(EDIT_TRANSACTION_FORM_FIELD.NUMBER, formattedValue);
	}, [unitsDecimalPoints, editTransactionForm]);

	// The currency comes off the resolved security, which falls back to the transaction's own — the
	// entity-scoped `securities` list does not carry it on a holder's page.
	const getCostLabelWithCurrency = () => {
		const baseLabel = costLabel || tCapManage('transactions.drawer.price.label');
		return selectedSecurity ? `${baseLabel} (${selectedSecurity.currency})` : baseLabel;
	};

	return (
		<ScrollArea>
			<FormProvider {...editTransactionForm}>
				<form onSubmit={editTransactionForm.handleSubmit(onSubmitDetails)}>
					<Group
						justify="apart"
						px="sm"
						py="xs"
						align="center"
						style={{
							borderBottom: `1px solid ${defaultTheme.v2.color.neutral[300]}`,
							justifyContent: 'flex-end',
						}}
					>
						{hasEditorPermission && (
							<Flex align="center" gap="0.5rem">
								{showSplitInterest && onSplitInterestClick && (
									<Button
										leftSection={<Icon variant="split" size="lg" />}
										variant="default"
										onClick={onSplitInterestClick}
										disabled={transaction?.deleted}
										data-testid="split-interest-button"
									>
										{tCapManage('transactions.split_interest.tooltip', {
											defaultValue: 'Update interest',
										})}
									</Button>
								)}
								<Button
									leftSection={<Icon variant="split" size="lg" />}
									variant="default"
									onClick={onSplitClick}
									disabled={transaction?.deleted}
								>
									{tCapManage('transactions.split.tooltip')}
								</Button>
								<Button
									leftSection={<Icon variant="pair" size="lg" />}
									variant="default"
									onClick={onPairClick}
									disabled={transaction?.deleted || shouldDisablePairButton}
								>
									{tCapManage('transactions.pair.tooltip')}
								</Button>
							</Flex>
						)}
					</Group>
					<EditableInfoContainer
						title={tCapManage('securities.details.edit_button')}
						isEdit={editMode}
						setIsEdit={setEditMode}
						// A deleted transaction cannot be edited, so do not offer an Edit that
						// cannot succeed. Restore it first.
						disableButtons={transaction?.deleted}
						onUpdate={editTransactionForm.handleSubmit(onSubmitDetails)}
						onCancel={handleCancel}
						hasEditorPermission={hasEditorPermission}
						hideRemove={true}
						skipTopBorder={true}
						useGrid={true}
						gridColumns={4}
					>
						<EditableInfoField
							label={tCapManage('transactions.drawer.date.label')}
							editMode={editMode}
							customValueElement={<CopyableDetailValue text={transactionDateText} />}
							formInput={
								<MantineDateInput
									name={EDIT_TRANSACTION_FORM_FIELD.DATE}
									maxDate={new Date()}
									size="md"
								/>
							}
						/>

						<EditableInfoField
							label={
								transactionType === TransactionType.CONVERSION_SHARE_CAPITAL
									? tCapManage('transactions.drawer.from.label')
									: tCapManage('transactions.drawer.security_name.label')
							}
							customValueElement={
								<CopyableDetailValue text={selectedSecurity?.name ?? LONG_DASH} />
							}
							formInput={
								<Select
									name={EDIT_TRANSACTION_FORM_FIELD.SECURITY_ID}
									control={editTransactionForm.control}
									// The entity-scoped list omits the transaction's own security on a
									// holder's page, which left the select blank; carry it as an option
									// so the field always shows what the transaction is on.
									data={securityNameSelectOptions}
									size="md"
									searchable
								/>
							}
							editMode={editMode}
						/>

						<EditableInfoField
							label={tCapManage('transactions.drawer.transaction_type.label')}
							customValueElement={
								<CopyableDetailValue
									text={getTransactionTypeText(transaction?.type, tCapManage)}
								/>
							}
							formInput={
								<Select
									name={EDIT_TRANSACTION_FORM_FIELD.TYPE}
									control={editTransactionForm.control}
									data={TRANSACTION_TYPE_OPTIONS}
									size="md"
									clearable={false}
								/>
							}
							editMode={editMode}
						/>

						{/* CAP-1976: the interest this transaction carries (Beneficial / Legal /
						    Both), editable here exactly as in the edit drawer — same rule, so the
						    dropdown appears whenever the interest is selectable and the read-only
						    field whenever there is a stored interest to show. A selectable interest
						    with none stored yet still gets the dropdown; the value reads as a dash
						    until one is chosen. */}
						{(showInterestSelect || interestText) && (
							<EditableInfoField
								label={tCapManage('transactions.drawer.interest.label')}
								customValueElement={
									<CopyableDetailValue text={interestText ?? LONG_DASH} />
								}
								formInput={
									showInterestSelect ? (
										<Select
											name={EDIT_TRANSACTION_FORM_FIELD.TITLE}
											control={editTransactionForm.control}
											data={
												isRedemptionOperation
													? operationInterestOptions
													: pairInterestOptions
											}
											data-testid={
												TEST_ELEMENT_IDS.CAP_TRANSACTION_EDIT_TRANSFER_INTEREST_SELECT
											}
											placeholder={tCapManage(
												'transactions.drawer.interest.placeholder'
											)}
											size="md"
											clearable={false}
											/* CAP-1976: editable in operation mode (the server
											   re-resolves the whole redemption). Outside it, visible
											   but disabled WITH the reason, never silently absent. */
											disabled={
												interestFixedByRedemptionGroup &&
												!isRedemptionOperation
											}
											description={
												isRedemptionOperation
													? undefined
													: interestFixedReason
											}
										/>
									) : (
										// Deliberately NOT the form-bound TextInput: the form holds
										// the raw TransactionTitle, so binding this field renders the
										// enum ("LEGAL_TITLE") instead of its label. The field is
										// read-only, so it has no business in the form at all — the
										// edit drawer's read-only branch does the same.
										// Only reachable with a stored interest — the outer gate needs one
										// of the two, and the dropdown owns the other branch.
										<MantineTextInput
											value={interestText ?? ''}
											readOnly
											disabled
											size="md"
										/>
									)
								}
								editMode={editMode}
							/>
						)}

						<EditableInfoField
							label="Transaction ID"
							customValueElement={
								<CopyableDetailValue
									text={transactionId || ''}
									displayText={
										transactionId
											? `${transactionId.slice(0, 8)}...${transactionId.slice(-4)}`
											: LONG_DASH
									}
								/>
							}
							formInput={
								<TextInput
									name={transactionId || ''}
									defaultValue={transactionId || ''}
									size="md"
									disabled={true}
								/>
							}
							editMode={editMode}
						/>

						{!hideBuyer && !isRedemptionOperation && (
							<EditableInfoField
								label={tCapManage('transactions.drawer.buyer.label')}
								customValueElement={
									<CopyableDetailValue
										text={getEntityNameById(
											transaction?.destinationId,
											personSelectOptions
										)}
									/>
								}
								formInput={
									<Select
										name={EDIT_TRANSACTION_FORM_FIELD.BUYER}
										placeholder={tCapManage(
											'transactions.drawer.buyer.placeholder'
										)}
										control={editTransactionForm.control}
										data={personSelectOptions}
										size="md"
										searchable
										disabled={hideBuyer}
										clearable={false}
									/>
								}
								editMode={editMode}
							/>
						)}

						{shouldShowHoldsFor({
							hideHoldsFor: hideBoughtFor,
							partyIsSecurityIssuer: hideBuyer,
							isManagementLoan,
							storedHoldsForId: transaction?.boughtForId,
						}) && (
							<EditableInfoField
								label={tCapManage('transactions.drawer.bought_for.label')}
								customValueElement={
									<CopyableDetailValue
										text={getEntityNameById(
											transaction?.boughtForId,
											personSelectOptions
										)}
									/>
								}
								formInput={
									<Select
										name={EDIT_TRANSACTION_FORM_FIELD.BOUGHT_FOR}
										placeholder={tCapManage(
											'transactions.drawer.bought_for.placeholder'
										)}
										control={editTransactionForm.control}
										data={boughtForOptions}
										size="md"
										searchable
										clearable={true}
									/>
								}
								editMode={editMode}
							/>
						)}

						{!hideBuyerMip && (
							<EditableInfoField
								label={tCapManage('transactions.drawer.buyer_mip.label')}
								customValueElement={
									<CopyableDetailValue
										text={getMipName(transaction?.toMipId, mipSelectOptions)}
									/>
								}
								formInput={
									<Select
										name={EDIT_TRANSACTION_FORM_FIELD.BUYER_MIP}
										control={editTransactionForm.control}
										placeholder={tCapManage(
											'transactions.drawer.buyer_mip.label'
										)}
										data={mipSelectOptions}
										size="md"
										searchable
										readOnly={!!transaction?.pgSaId}
										disabled={!!transaction?.pgSaId || hideBuyerMip}
										clearable={false}
									/>
								}
								editMode={editMode}
							/>
						)}

						{!hideSeller && (
							<EditableInfoField
								label={tCapManage('transactions.drawer.seller.label')}
								customValueElement={
									<CopyableDetailValue
										text={getEntityNameById(
											transaction?.sourceId,
											personSelectOptions
										)}
									/>
								}
								formInput={
									<Select
										name={EDIT_TRANSACTION_FORM_FIELD.SELLER}
										control={editTransactionForm.control}
										placeholder={tCapManage(
											'transactions.drawer.seller.placeholder'
										)}
										data={personSelectOptions}
										size="md"
										searchable
										disabled={hideSeller}
										clearable={false}
									/>
								}
								editMode={editMode}
							/>
						)}

						{shouldShowHoldsFor({
							hideHoldsFor: hideSoldFor,
							partyIsSecurityIssuer: hideSeller,
							isManagementLoan,
							storedHoldsForId: transaction?.soldForId,
						}) && (
							<EditableInfoField
								label={tCapManage('transactions.drawer.sold_for.label')}
								customValueElement={
									<CopyableDetailValue
										text={getEntityNameById(
											transaction?.soldForId,
											personSelectOptions
										)}
									/>
								}
								formInput={
									<Select
										name={EDIT_TRANSACTION_FORM_FIELD.SOLD_FOR}
										control={editTransactionForm.control}
										placeholder={tCapManage(
											'transactions.drawer.sold_for.placeholder'
										)}
										data={soldForOptions}
										size="md"
										searchable
										clearable={true}
									/>
								}
								editMode={editMode}
							/>
						)}

						{!hideSellerMip && (
							<EditableInfoField
								label={tCapManage('transactions.drawer.seller_holding.label')}
								customValueElement={
									<CopyableDetailValue
										text={getMipName(transaction?.fromMipId, mipSelectOptions)}
									/>
								}
								formInput={
									<Select
										name={EDIT_TRANSACTION_FORM_FIELD.SELLER_MIP}
										control={editTransactionForm.control}
										placeholder={tCapManage(
											'transactions.drawer.seller_holding.placeholder'
										)}
										data={sellerMipSelectOptions}
										size="md"
										searchable
										// A single option is no choice — the drawer locks the field on
										// the same rule.
										readOnly={
											!!transaction?.pgSaId ||
											sellerMipSelectOptions.length <= 1
										}
										disabled={
											!!transaction?.pgSaId ||
											hideSellerMip ||
											sellerMipSelectOptions.length <= 1
										}
										clearable={false}
									/>
								}
								editMode={editMode}
							/>
						)}

						{!hideUnits && (
							<EditableInfoField
								label={tCapManage('transactions.drawer.number_of_units.label')}
								customValueElement={<CopyableDetailValue text={transactionUnits} />}
								formInput={
									<NumberInput
										key={`units-${unitsDecimalPoints}`}
										name={EDIT_TRANSACTION_FORM_FIELD.NUMBER}
										control={editTransactionForm.control}
										size="md"
										decimalScale={unitsDecimalPoints}
									/>
								}
								editMode={editMode}
							/>
						)}

						<EditableInfoField
							label={getCostLabelWithCurrency()}
							customValueElement={
								<CopyableDetailValue
									text={
										transaction?.cost
											? formatNumber({
													value: transaction.cost,
													lockPrecision: true,
													minPrecision: 2,
													maxPrecision: 2,
												})
											: LONG_DASH
									}
								/>
							}
							formInput={
								<NumberInput
									name={EDIT_TRANSACTION_FORM_FIELD.PRICE}
									control={editTransactionForm.control}
									size="md"
									step={0.01}
									decimalScale={2}
									disabled={hideCost}
								/>
							}
							editMode={editMode}
						/>

						{transactionType === TransactionType.CONVERSION_SHARE_CAPITAL && (
							<EditableInfoField
								label={tCapManage('transactions.drawer.to.label')}
								customValueElement={
									<CopyableDetailValue
										text={getSecurityNameById(
											(transaction as { conversionTargetSecurityId?: string })
												?.conversionTargetSecurityId ?? '',
											securities
										)}
									/>
								}
								formInput={
									<Select
										name={
											EDIT_TRANSACTION_FORM_FIELD.CONVERSION_TARGET_SECURITY_ID
										}
										control={editTransactionForm.control}
										data={securityNameSelectOptions}
										size="md"
										searchable
									/>
								}
								editMode={editMode}
							/>
						)}

						<EditableInfoField
							label={participationThresholdExpressedAsLabel}
							customValueElement={
								<CopyableDetailValue
									text={
										disableParticipationThresholdFields ||
										!transaction?.participationThresholdExpressedAs
											? LONG_DASH
											: normalizeScreamingSnakeToUI(
													transaction?.participationThresholdExpressedAs ||
														''
												)
									}
								/>
							}
							formInput={
								<Select
									name={
										EDIT_TRANSACTION_FORM_FIELD.PARTICIPATION_THRESHOLD_EXPRESSED_AS
									}
									control={editTransactionForm.control}
									data={TRANSACTION_PARTICIPATION_THRESHOLD_EXPRESSED_AS_OPTIONS}
									size="md"
									clearable={false}
									disabled={disableParticipationThresholdFields}
								/>
							}
							editMode={editMode}
						/>

						<EditableInfoField
							label={participationThresholdLabel}
							customValueElement={
								<CopyableDetailValue
									text={
										disableParticipationThresholdFields ||
										!transaction?.participationThreshold
											? LONG_DASH
											: formatNumber({
													value: transaction.participationThreshold,
													lockPrecision: true,
													minPrecision: 5,
													maxPrecision: 5,
												})
									}
								/>
							}
							formInput={
								<NumberInput
									name={EDIT_TRANSACTION_FORM_FIELD.PARTICIPATION_THRESHOLD}
									control={editTransactionForm.control}
									size="md"
									step={0.01}
									decimalScale={5}
									disabled={disableParticipationThresholdFields}
								/>
							}
							editMode={editMode}
						/>
					</EditableInfoContainer>
				</form>
			</FormProvider>

			{operationRailNote && (
				<Alert color="gray" variant="light" mt="md">
					<Text size="sm">{operationRailNote}</Text>
				</Alert>
			)}

			{redemptionCounterparts.length > 0 && (
				<Alert
					color="blue"
					variant="light"
					mt="md"
					title={tCapManage('transactions.drawer.retires_alongside.title')}
				>
					<Stack gap={4}>
						{redemptionCounterparts.map((counterpart) => (
							<Text key={`${counterpart.holderName}-${counterpart.title}`} size="sm">
								{counterpart.holderName} (
								{getTransactionInterestText(counterpart.title, tCapManage)}
								), {counterpart.units}
							</Text>
						))}
						{redemptionSegmentCount > 1 && (
							<Text size="xs" c="dimmed">
								{tCapManage('transactions.drawer.retires_alongside.spread', {
									count: redemptionSegmentCount,
								})}
							</Text>
						)}
					</Stack>
				</Alert>
			)}

			{/* CAP-2039: what the values currently in the form would retire, as against what the
			    group retires today (the panel above). Kept in step with EditTransactionDrawer, which
			    renders the same three blocks for the same controller. */}
			<RedemptionCounterpartPanel
				preview={redemptionCounterpartPreview}
				interest={redemptionPreviewInterest}
				securityName={selectedSecurity?.name ?? ''}
			/>

			{pendingRedemption ? (
				<InseparableRedemptionModal
					disclosure={pendingRedemption.disclosure}
					onProceed={() => void confirmRedemptionOperation()}
					onCancel={cancelRedemptionOperation}
					isSubmitting={isRedemptionOperationPending}
				/>
			) : null}

			{interestPairModal ? (
				<InterestPairEditModal
					plan={interestPairModal.plan}
					onProceed={interestPairModal.onProceed ?? closeInterestPairModal}
					onClose={closeInterestPairModal}
					onDeletePair={onDeletePair}
				/>
			) : null}
		</ScrollArea>
	);
};

interface LabelWithTooltipProps {
	labelText: string;
	tooltipLabel: string;
}

const LabelWithTooltip = ({ labelText, tooltipLabel }: LabelWithTooltipProps) => (
	<Flex gap="0.5rem">
		{labelText}

		<Tooltip label={tooltipLabel}>
			<Flex c="dark.8" className="tooltip-icon">
				<Icon variant="information" size="lg" />
			</Flex>
		</Tooltip>
	</Flex>
);
